import {svgIconClasses} from '@mui/material/SvgIcon';
import {typographyClasses} from '@mui/material/Typography';
import {buttonBaseClasses} from '@mui/material/ButtonBase';
import {chipClasses} from '@mui/material/Chip';
import {iconButtonClasses} from '@mui/material/IconButton';
import {alpha, darken} from '@mui/material/styles';

import {gray, blue, successGreen, warningOrange} from '../themePrimitives';
import {bluePulse} from '../animations';

export const dataDisplayCustomizations = {
    MuiBadge: {
        styleOverrides: {
            badge: {
                // The warning and success fills, the same as the chips beside them
                // (see warningOrange / successGreen in themePrimitives).
                '&.MuiBadge-colorWarning': {
                    backgroundColor: warningOrange.fill,
                    color: warningOrange.onFill
                },
                '&.MuiBadge-colorSuccess': {
                    backgroundColor: successGreen.fill,
                    color: '#fff'
                }
            }
        }
    },
    MuiList: {
        styleOverrides: {
            root: {
                padding: '8px',
                display: 'flex',
                flexDirection: 'column',
                gap: 0
            }
        }
    },
    MuiListItem: {
        styleOverrides: {
            root: ({theme}) => ({
                '.menu-nav &': {
                    [`& .${svgIconClasses.root}`]: {
                        width: '1rem',
                        height: '1rem',
                        color: (theme.vars || theme).palette.text.secondary
                    },
                    [`& .${typographyClasses.root}`]: {
                        fontWeight: 500
                    },
                    [`& .${buttonBaseClasses.root}`]: {
                        display: 'flex',
                        gap: 8,
                        padding: '2px 8px',
                        borderRadius: (theme.vars || theme).shape.borderRadius,
                        opacity: 0.7,
                        transition: theme.transitions.create('background-color', {
                            duration: theme.transitions.duration.shortest
                        }),
                        '&.Mui-selected': {
                            opacity: 1,
                            backgroundColor: alpha(theme.palette.primary.main, 0.05),
                            [`& .${svgIconClasses.root}`]: {
                                color: (theme.vars || theme).palette.text.primary
                            },
                            '&:focus-visible': {
                                backgroundColor: alpha(theme.palette.action.selected, 0.3)
                            },
                            '&:hover': {
                                backgroundColor: alpha(theme.palette.primary.main, 0.1)
                            }
                        },
                        '&:focus-visible': {
                            backgroundColor: 'transparent'
                        }
                    }
                },
                '&:last-child': {
                    borderBottom: 0
                }
            })
        }
    },
    MuiListItemText: {
        styleOverrides: {
            primary: ({theme}) => ({
                fontSize: theme.typography.body2.fontSize,
                fontWeight: 500,
                lineHeight: theme.typography.body2.lineHeight
            }),
            secondary: ({theme}) => ({
                fontSize: theme.typography.caption.fontSize,
                lineHeight: theme.typography.caption.lineHeight
            })
        }
    },
    MuiListSubheader: {
        styleOverrides: {
            root: ({theme}) => ({
                // The tinted band spans the full menu width: menus run their list
                // edge-to-edge (navigation.js) and items carry their own margin, so
                // the header needs no horizontal margin of its own. paddingLeft: 16
                // aligns the header text with the items' (8px margin + 8px padding)
                // inset. Vertical margins set the band off from the rows around it.
                marginTop: 8,
                marginBottom: 8,
                backgroundColor: gray[50],
                padding: '6px 16px',
                fontSize: theme.typography.caption.fontSize,
                fontWeight: 600,
                lineHeight: theme.typography.caption.lineHeight,
                letterSpacing: '.06em',
                color: (theme.vars || theme).palette.text.secondary,
                textTransform: 'uppercase',
                // Divider above and below the band. No border-radius: on a
                // top/bottom-only border the rounded corners bent inward and rendered
                // as odd curved line ends inside Select menus.
                borderTop: `1px solid ${(theme.vars || theme).palette.divider}`,
                borderBottom: `1px solid ${(theme.vars || theme).palette.divider}`,
                // No extra gap above the very first row, so a leading header sits
                // flush with the top of the menu.
                '&:first-of-type': {
                    marginTop: 0,
                    borderTop: 'none'
                }
            })
        }
    },
    MuiListItemIcon: {
        styleOverrides: {
            root: {
                minWidth: 0
            }
        }
    },
    MuiChip: {
        // defaultProps: {
        //     size: 'small'
        // },
        styleOverrides: {
            root: ({theme}) => ({
                // border: '1px solid',
                // borderRadius: '999px',
                [`& .${chipClasses.label}`]: {
                    fontWeight: 600
                },
                padding: '1px 2px',
                // `className="pulse"` composes with any color to draw attention
                // to an in-progress state. --pulse-color is set per palette
                // color so the ring matches the chip color rather than always
                // being blue.
                '&.pulse': {
                    animation: `${bluePulse} 1.8s ease-in-out infinite`,
                    '--pulse-color-fade': 'transparent',
                    '&.MuiChip-colorPrimary': {'--pulse-color': alpha(theme.palette.primary.main, 0.5)},
                    '&.MuiChip-colorSecondary': {'--pulse-color': alpha(theme.palette.secondary.main, 0.5)},
                    // Info pulse keyed to blue[400] — the same value palette.info.main
                    // now resolves to, and what the info chip renders as.
                    '&.MuiChip-colorInfo': {'--pulse-color': alpha(blue[400], 0.5)},
                    '&.MuiChip-colorSuccess': {'--pulse-color': alpha(theme.palette.success.main, 0.5)},
                    '&.MuiChip-colorWarning': {'--pulse-color': alpha(theme.palette.warning.main, 0.5)},
                    '&.MuiChip-colorError': {'--pulse-color': alpha(theme.palette.error.main, 0.5)}
                },
                variants: [
                    {
                        props: {
                            color: 'default'
                        },
                        style: {
                            borderColor: gray[200],
                            backgroundColor: gray[100],
                            [`& .${chipClasses.label}`]: {
                                color: gray[700]
                            },
                            [`& .${chipClasses.icon}, & .${chipClasses.deleteIcon}`]: {
                                color: gray[700]
                            },
                            [`& .${chipClasses.deleteIcon}:hover`]: {
                                color: gray[900]
                            }
                        }
                    },
                    {
                        props: {
                            color: 'success'
                        },
                        style: {
                            borderColor: successGreen.fill,
                            backgroundColor: successGreen.fill,
                            [`& .${chipClasses.label}`]: {
                                color: '#fff'
                            },
                            [`& .${chipClasses.icon}, & .${chipClasses.deleteIcon}, & .MuiSvgIcon-root`]: {
                                color: '#fff'
                            },
                            [`& .${chipClasses.deleteIcon}:hover`]: {
                                color: alpha('#fff', 0.8)
                            },
                            // Only clickable / link chips get a hover tint.
                            [`&.${chipClasses.clickable}:hover`]: {
                                backgroundColor: `${successGreen.fillHover} !important`
                            }
                        }
                    },
                    {
                        props: {
                            color: 'warning'
                        },
                        style: {
                            // Outlined too — there is no separate outlined warning look.
                            borderColor: warningOrange.fill,
                            backgroundColor: warningOrange.fill,
                            [`& .${chipClasses.label}`]: {
                                color: warningOrange.onFill
                            },
                            [`& .${chipClasses.icon}, & .${chipClasses.deleteIcon}, & .MuiSvgIcon-root`]: {
                                color: warningOrange.onFill
                            },
                            [`& .${chipClasses.deleteIcon}:hover`]: {
                                color: '#000'
                            },
                            [`&.${chipClasses.clickable}:hover`]: {
                                backgroundColor: `${warningOrange.fillHover} !important`
                            }
                        }
                    },
                    {
                        props: {
                            color: 'error'
                        },
                        style: {
                            borderColor: '#d3302f',
                            backgroundColor: '#d3302f',
                            [`& .${chipClasses.label}`]: {
                                color: '#fff'
                            },
                            [`& .${chipClasses.icon}, & .${chipClasses.deleteIcon}, & .MuiSvgIcon-root`]: {
                                color: '#fff'
                            },
                            [`& .${chipClasses.deleteIcon}:hover`]: {
                                color: alpha('#fff', 0.8)
                            },
                            // Only clickable / link chips get a hover tint.
                            [`&.${chipClasses.clickable}:hover`]: {
                                backgroundColor: `${darken('#d3302f', 0.1)} !important`
                            }
                        }
                    },
                    {
                        props: {
                            color: 'info'
                        },
                        style: {
                            borderColor: blue[400],
                            backgroundColor: blue[400],
                            [`& .${chipClasses.label}`]: {
                                color: '#fff'
                            },
                            [`& .${chipClasses.icon}, & .${chipClasses.deleteIcon}, & .MuiSvgIcon-root`]: {
                                color: '#fff'
                            },
                            [`& .${chipClasses.deleteIcon}:hover`]: {
                                color: alpha('#fff', 0.8)
                            },
                            // Only clickable / link chips get a hover tint.
                            [`&.${chipClasses.clickable}:hover`]: {
                                backgroundColor: `${darken(blue[400], 0.2)} !important`
                            }
                        }
                    },
                    {
                        props: {size: 'small'},
                        style: {
                            maxHeight: 20,
                            [`& .${chipClasses.label}`]: {
                                fontSize: theme.typography.caption.fontSize
                            },
                            [`& .${svgIconClasses.root}`]: {
                                fontSize: theme.typography.caption.fontSize
                            }
                        }
                    },
                    {
                        props: {size: 'medium'},
                        style: {
                            height: 29,
                            [`& .${chipClasses.label}`]: {
                                fontSize: theme.typography.caption.fontSize
                            }
                        }
                    },
                    {
                        props: {
                            variant: 'tooltip'
                        },
                        style: {
                            backgroundColor: (theme.vars || theme).palette.Tooltip.bg,
                            fontSize: theme.typography.caption.fontSize,
                            padding: 2,
                            height: 'auto',
                            '& .MuiChip-label': {
                                color: '#fff',
                                paddingLeft: 6,
                                paddingRight: 6
                            }
                        }
                    }
                ]
            })
        }
    },
    MuiTablePagination: {
        styleOverrides: {
            actions: {
                display: 'flex',
                gap: 8,
                marginRight: 6,
                [`& .${iconButtonClasses.root}`]: {
                    minWidth: 0,
                    width: 36,
                    height: 36
                }
            }
        }
    },
    MuiIcon: {
        defaultProps: {
            fontSize: 'small'
        },
        styleOverrides: {
            root: {
                variants: [
                    {
                        props: {
                            fontSize: 'small'
                        },
                        style: {
                            fontSize: '1rem'
                        }
                    }
                ]
            }
        }
    },
    MuiTableContainer: {
        styleOverrides: {
            root: {
                '&.MuiPaper-root': {
                    border: '1px solid',
                    borderColor: gray[200],
                    overflow: 'hidden'
                }
            }
        }
    },
    MuiTableFooter: {
        styleOverrides: {
            root: {
                borderTop: '1px solid',
                borderColor: gray[200]
            }
        }
    },
    MuiTable: {
        styleOverrides: {
            root: {
                '& th': {
                    borderColor: gray[200]
                },
                '& tr': {
                    borderBottom: '1px solid',
                    borderColor: gray[100],
                    '&:last-child': {
                        borderBottom: 'none'
                    }
                },
                '& td': {
                    borderColor: gray[100],
                    backgroundColor: alpha('#fff', 0.6)
                },
                '& tr:nth-of-type(odd) td': {
                    backgroundColor: alpha(gray[50], 0.5)
                }
            }
        }
    },
    MuiTableCell: {
        styleOverrides: {
            root: ({theme}) => ({
                padding: theme.spacing(1)
            })
        }
    }
    // MuiTableRow: {
    //     styleOverrides: {
    //         root: {
    //             borderBottom: '1px solid',
    //             borderColor: gray[200],
    //             '&:last-child': {
    //                 '& .MuiTableCell-root': {
    //                     borderBottom: 'none'
    //                 }
    //             }
    //         }
    //     }
    // },
};
